/**
 * Acquisition Category Data Service
 * Follows data engineer agent principles:
 * - Schema-aware operations
 * - Proper error handling
 * - Performance optimization
 * - Consistent patterns with CategoryDataService
 */

export interface AcquisitionCategoryData {
  name: string;
  amount: number;
  count: number;
  companies?: number;
}

export interface AcquisitionCategoryApiResponse {
  chart_type: string;
  title: string;
  subtitle: string;
  categories: AcquisitionCategoryData[];
  metadata: {
    timeframe: string;
    week_offset: number;
    total_acquisitions: number;
    category_count: number;
    current_period: {
      start: string;
      end: string;
    };
    generated_at: string;
  };
  cache_status?: string;
  response_time_ms?: number;
  filters_applied?: Record<string, string[]> | null;
}

export class AcquisitionCategoryService {
  private baseUrl: string;
  private cache = new Map<string, { data: AcquisitionCategoryApiResponse; timestamp: number }>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor(baseUrl?: string) {
    // Use environment variable for API endpoint
    this.baseUrl = baseUrl || import.meta.env.VITE_API_ENDPOINT || 'https://spog-aggregation.mike-532.workers.dev';
  }

  /**
   * Get acquisition category data with caching and error handling
   */
  async getAcquisitionCategoryData(timeframe: string = '12w', weekOffset: number = 0, filters?: Record<string, string[] | undefined>): Promise<AcquisitionCategoryApiResponse> {
    // Build filter query string
    const filterParams = filters
      ? Object.entries(filters)
          .filter(([, values]) => values && values.length > 0)
          .map(([key, values]) => `${key}=${values!.join(',')}`)
          .join('&')
      : '';

    const cacheKey = `${timeframe}-${weekOffset}${filterParams ? `-${filterParams}` : ''}`;

    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.data;
    }

    try {
      let url = `${this.baseUrl}/api/v2/acquisition-category-data?timeframe=${encodeURIComponent(timeframe)}&week_offset=${encodeURIComponent(weekOffset)}`;
      if (filterParams) {
        url += `&${filterParams}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: AcquisitionCategoryApiResponse = await response.json();

      // Cache the result
      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });

      return data;
    } catch (error) {
      console.error('Failed to fetch acquisition category data:', error);
      throw new Error(`Failed to load acquisition category data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Clear specific cache entry
   */
  clearCacheEntry(timeframe: string, weekOffset: number, filters?: Record<string, string[] | undefined>): void {
    const filterParams = filters
      ? Object.entries(filters)
          .filter(([, values]) => values && values.length > 0)
          .map(([key, values]) => `${key}=${values!.join(',')}`)
          .join('&')
      : '';
    const cacheKey = `${timeframe}-${weekOffset}${filterParams ? `-${filterParams}` : ''}`;
    this.cache.delete(cacheKey);
  }
}

// Export singleton instance
export const acquisitionCategoryService = new AcquisitionCategoryService();
